import { useState, useEffect, useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { resolveUrl, isUploadedVideo, getEmbedUrl, mediaItems, imageUrls } from "../../lib/media";
import { useScrollLock } from "../../lib/useScrollLock";

// Modern masonry gallery (photos + videos) with fullscreen lightbox for photos.
export const PhotoMasonry = ({ images = [], onOpen }) => {
  const media = mediaItems(images);
  const imgs = media.filter((m) => m.type === "image").map((m) => m.url);
  if (!media.length) return null;
  return (
    <div className="columns-2 md:columns-3 gap-4 [column-fill:_balance]">
      {media.map((m, i) => (
        m.type === "video" ? (
          <div key={i} data-testid={`photo-${i}`} className="mb-4 block w-full overflow-hidden rounded-lg border border-white/10 bg-black break-inside-avoid">
            {isUploadedVideo(m.url)
              ? <video src={resolveUrl(m.url)} poster={resolveUrl(m.poster) || undefined} controls playsInline preload="metadata" className="w-full h-auto" />
              : <div className="aspect-video"><iframe src={getEmbedUrl(m.url)} title="Vidéo" loading="lazy" allowFullScreen className="w-full h-full" /></div>}
          </div>
        ) : (
          <button key={i} onClick={() => onOpen(imgs.indexOf(m.url))} data-testid={`photo-${i}`}
            className="mb-4 block w-full overflow-hidden rounded-lg border border-white/10 bg-[#0e0e0e] group break-inside-avoid">
            <img src={resolveUrl(m.url)} alt="" loading="lazy" className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
          </button>
        )
      ))}
    </div>
  );
};

// Lightbox plein écran — navigation FLUIDE : l'image cible + ses voisines sont préchargées,
// et l'image courante reste affichée tant que la suivante n'est pas prête (jamais de cadre vide).
// Les clics rapides sur les flèches déplacent la CIBLE ; l'affichage suit dès que possible.
export const Lightbox = ({ images = [], index, onClose }) => {
  const pics = imageUrls(images);
  const [i, setI] = useState(index || 0);        // cible demandée (flèches / clics rapides)
  const [shown, setShown] = useState(index || 0); // image affichée (garantie chargée)
  const loaded = useRef(new Set());               // URLs déjà en cache cette session
  const target = useRef(index || 0);
  useScrollLock(index !== null);
  useEffect(() => { setI(index || 0); setShown(index || 0); target.current = index || 0; }, [index]);

  const markLoaded = useCallback((u) => { if (u) loaded.current.add(u); }, []);

  // Précharge intelligente : cible + suivante + précédente (prioritaires), jamais deux fois.
  useEffect(() => {
    if (index === null || !pics.length) return;
    [i, (i + 1) % pics.length, (i - 1 + pics.length) % pics.length].forEach((x) => {
      const u = pics[x];
      if (!u || loaded.current.has(u)) return;
      const im = new Image();
      im.onload = () => markLoaded(u);
      im.onerror = () => markLoaded(u); // ne jamais rester bloqué sur une image cassée
      im.src = resolveUrl(u);
    });
  }, [i, index, pics, markLoaded]);

  // Bascule l'affichage vers la cible SEULEMENT quand elle est téléchargée.
  useEffect(() => {
    target.current = i;
    const u = pics[i];
    if (!u) return;
    if (loaded.current.has(u)) { setShown(i); return; }
    let alive = true;
    const im = new Image();
    im.onload = im.onerror = () => { markLoaded(u); if (alive && target.current === i) setShown(i); };
    im.src = resolveUrl(u);
    // garde-fou : après 5 s on affiche quand même (connexion très lente, JPEG progressif)
    const t = setTimeout(() => { if (alive && target.current === i) setShown(i); }, 5000);
    return () => { alive = false; clearTimeout(t); };
  }, [i, pics, markLoaded]);

  const prev = useCallback((e) => { e?.stopPropagation(); setI((x) => (x - 1 + pics.length) % pics.length); }, [pics.length]);
  const next = useCallback((e) => { e?.stopPropagation(); setI((x) => (x + 1) % pics.length); }, [pics.length]);
  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowLeft") prev(); if (e.key === "ArrowRight") next(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, onClose, prev, next]);

  const waiting = shown !== i;

  return (
    <AnimatePresence>
      {index !== null && pics.length > 0 && (
        <motion.div className="fixed inset-0 z-[10000] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="photo-lightbox">
          <button onClick={onClose} data-testid="lightbox-close" className="absolute top-6 right-6 h-11 w-11 rounded-full border border-white/20 text-white/80 flex items-center justify-center hover:border-electric hover:text-electric transition-colors"><X size={22} /></button>
          {pics.length > 1 && <>
            <button onClick={prev} data-testid="lightbox-prev" className="absolute left-4 md:left-8 h-12 w-12 rounded-full border border-white/20 text-white/80 flex items-center justify-center hover:border-electric hover:text-electric transition-colors"><ChevronLeft size={24} /></button>
            <button onClick={next} data-testid="lightbox-next" className="absolute right-4 md:right-8 h-12 w-12 rounded-full border border-white/20 text-white/80 flex items-center justify-center hover:border-electric hover:text-electric transition-colors"><ChevronRight size={24} /></button>
          </>}
          <motion.img key={shown} src={resolveUrl(pics[shown])} alt="" onClick={(e) => e.stopPropagation()} onLoad={() => markLoaded(pics[shown])}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }} decoding="async"
            data-testid="lightbox-image" data-idx={shown}
            className="max-h-[88vh] max-w-[92vw] rounded-lg object-contain shadow-2xl ring-1 ring-white/15" />
          {waiting && (
            <span className="absolute bottom-6 right-6 text-electric" data-testid="lightbox-loading"><Loader2 size={20} className="animate-spin" /></span>
          )}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs tracking-[0.2em] text-white/60">{i + 1} / {pics.length}</div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
